import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { PerfilUsuario } from '../remittance-hub/models/remittance.model';
import * as RR from '../remittance-hub/rules/remittance-rules';
import {
  EscalonVista,
  HomeRatesState,
  ProductoTasa,
  TasaHome,
} from './models/home-rates.model';
import * as R from './rules/home-rates.rules';
import { HomeRatesService } from './services/home-rates.service';

/**
 * Tarjeta de tasas del home.
 *
 * Muestra los corredores que el cliente puede usar según su país y los
 * refresca cada 3 minutos. Es INFORMATIVA: tocar una fila lleva al cotizador
 * con el par preseleccionado, nunca precarga un monto ni salta pasos — los
 * rieles propios no tienen deduplicación de envíos y este flujo no puede
 * terminar disparando uno.
 *
 * Todo el trabajo lo hace `HomeRatesService`; acá sólo viven el plegado y la
 * navegación.
 */
@Component({
  selector: 'app-home-rates',
  templateUrl: './home-rates.component.html',
  styleUrls: ['./home-rates.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeRatesComponent implements OnInit, OnDestroy {
  /**
   * Sólo los corredores que salen de un endpoint sin token. Es lo que ve el
   * cliente que todavía no completó la validación.
   */
  @Input() soloPublicas = false;

  readonly estado$ = this.rates.estado$;

  /**
   * Siempre plegada al entrar y en cada recarga. El home abre mostrando
   * saldos y acciones; las tasas las despliega el cliente cuando quiere, y
   * el estado NO se recuerda a propósito.
   */
  colapsada = true;

  /**
   * Producto abierto del acordeón: uno a la vez. Vive sólo en memoria y
   * arranca cerrado.
   */
  abierto: string | null = null;

  /**
   * Monto del simulador por producto, sólo en memoria. NUNCA viaja: «Enviar» /
   * «Comprar» abre el cotizador sin monto, como siempre (`abrir()`).
   */
  montoSimulado: Record<string, number> = {};
  /** Lo que el cliente escribió, tal cual (para no mover el cursor al formatear). */
  montoTexto: Record<string, string> = {};

  /** Productos ya armados por grupo. Se recalculan sólo cuando llega estado. */
  vista: {
    envios: ProductoTasa[];
    otros: ProductoTasa[];
    usdt: ProductoTasa[];
  } = { envios: [], otros: [], usdt: [] };

  private sub = new Subscription();
  private registrado = false;

  constructor(
    private rates: HomeRatesService,
    private router: Router,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.sub.add(
      this.store.select('auth').subscribe(({ user }: any) => {
        if (!user) return;

        const perfil: PerfilUsuario = RR.perfilDeTipoId(user?.tipoId);

        if (!this.registrado) {
          this.registrado = true;
          this.rates.registrar();
        }

        this.rates.setPerfil(perfil, this.soloPublicas);
        this.cdr.markForCheck();
      }),
    );

    this.sub.add(
      this.estado$.subscribe((st) => {
        this.vista = this.armarVista(st);
        // Si el producto abierto dejó de existir (p. ej. el par volvió a ser
        // una sola fila porque ya no llegan tramos), se cierra.
        if (this.abierto && !this.existe(this.abierto)) this.abierto = null;
        this.cdr.markForCheck();
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    if (this.registrado) this.rates.soltar();
  }

  // ───────────────────────── Plegado ─────────────────────────

  toggleTarjeta(): void {
    this.colapsada = !this.colapsada;
  }

  // ───────────────────────── Tramos ─────────────────────────

  /**
   * Rótulo de la fila del riel propio. Con los tres tramos a la vista, repetir
   * el primero arriba era ruido: la fila dice la unidad y los números viven en
   * los recuadros.
   */
  unidad(t: TasaHome): string {
    return R.etiquetaUnidad(t.def);
  }

  /** La fila muestra compra y venta en vez de un solo número. */
  esPar(t: TasaHome): boolean {
    return R.esPar(t.def);
  }

  // ───────────────────────── Agrupación ─────────────────────────

  /**
   * Envíos: el riel propio primero y los otros destinos a continuación. Van en
   * una sola lista porque para el cliente son lo mismo —mandar plata a un
   * país— aunque por dentro sean rieles distintos. Los destinos que el
   * proveedor tiene apagados no llegan hasta acá.
   */
  envios(st: HomeRatesState): TasaHome[] {
    return [...st.nivel1.filter((t) => t.def.grupo === 'ENVIO'), ...st.nivel2];
  }

  /** Conversiones dentro de la billetera. */
  conversiones(st: HomeRatesState): TasaHome[] {
    return st.nivel1.filter((t) => t.def.grupo === 'USDT');
  }

  // ───────────────────────── Productos (acordeón) ─────────────────────────

  /**
   * Arma los tres bloques de la tarjeta: el riel propio arriba, los demás
   * envíos dentro de «Otros destinos» y las conversiones USDT (el par partido
   * en Comprar/Vender sólo si llegan tramos).
   */
  armarVista(st: HomeRatesState): { envios: ProductoTasa[]; otros: ProductoTasa[]; usdt: ProductoTasa[] } {
    const envios = this.envios(st);
    const principales = envios.filter((t) => R.esPublico(t.def));
    const otros = envios.filter((t) => !R.esPublico(t.def));
    return {
      envios: this.aplanar(principales),
      otros: this.aplanar(otros),
      usdt: this.aplanar(this.conversiones(st)),
    };
  }

  private aplanar(filas: TasaHome[]): ProductoTasa[] {
    const out: ProductoTasa[] = [];
    filas.forEach((t) => out.push(...R.productosDe(t)));
    return out;
  }

  private existe(id: string): boolean {
    return [...this.vista.envios, ...this.vista.usdt].some((p) => p.id === id && !!p.escalera);
  }

  trackProducto(_: number, p: ProductoTasa): string {
    return p.id;
  }

  /** Un producto abierto a la vez; tocar el abierto lo cierra. */
  toggleProducto(p: ProductoTasa): void {
    this.abierto = this.abierto === p.id ? null : p.id;
  }

  /** El par de siempre, sin tramos: «Compras a · Vendes a». */
  esParComoHoy(p: ProductoTasa): boolean {
    return R.esPar(p.fila.def) && p.id === p.fila.def.id;
  }

  /** Id seguro para el DOM (los ids de corredor llevan `:` y `>`). */
  domId(p: ProductoTasa): string {
    return 'hr-' + p.id.replace(/[^A-Za-z0-9_-]/g, '-');
  }

  /** El número grande: la tasa del primer tramo, la que recibe la mayoría, en es-CL. */
  desde(p: ProductoTasa): string {
    return R.formatearTasa(R.tasaInicial(p.escalera), p.decimales);
  }

  /** «Mejor X desde Y» bajo el nombre; null si no hay tramo mejor que el primero. */
  mejor(p: ProductoTasa): string | null {
    return R.rotuloMejor(p.escalera, p.monedaMonto, p.base, p.decimales);
  }

  /**
   * «Compras a · Vendes a» del par sin tramos, en es-CL como el resto de la
   * tarjeta (el pipe `number` usaba el LOCALE_ID en-US: «1,234.56» al lado
   * de «1.234,56»). Sin dato, «—».
   */
  numeroPar(v: number | null | undefined, t: TasaHome): string {
    return R.formatearTasa(v, t.def.decimales);
  }

  /** Número de una fila sin tramos, en es-CL. */
  valor(p: ProductoTasa): string {
    return R.formatearTasa(p.tasa, p.decimales);
  }

  alturaMini(i: number, n: number): number {
    return R.alturaMini(i, n);
  }

  trackEscalon(i: number, e: EscalonVista): string {
    return `${i}:${e.desde}`;
  }

  // ───────────────────────── Simulador (sólo en memoria) ─────────────────────────

  /** Lo que escribe el cliente, en es-CL: «1.500.000» o «1.500,50». */
  alEscribir(p: ProductoTasa, texto: string): void {
    this.montoTexto[p.id] = texto;
    this.montoSimulado[p.id] = parsearMonto(texto);
  }

  /**
   * Al salir del campo, el monto queda escrito en es-CL («1.500.000»), el
   * mismo formato de los chips, las tasas y la escalera de la tarjeta.
   */
  alSalir(p: ProductoTasa): void {
    const v = this.montoSimulado[p.id] || 0;
    if (v > 0) this.montoTexto[p.id] = new Intl.NumberFormat('es-CL', { maximumFractionDigits: 2 }).format(v);
  }

  /** Un monto típico de un toque. */
  elegirMonto(p: ProductoTasa, v: number): void {
    this.montoSimulado[p.id] = v;
    this.montoTexto[p.id] = new Intl.NumberFormat('es-CL', { maximumFractionDigits: 2 }).format(v);
  }

  monto(p: ProductoTasa): number {
    return this.montoSimulado[p.id] || 0;
  }

  /** Rótulo de un chip: «$500.000», «Bs. 600.000», «1.000 USDT». */
  chip(p: ProductoTasa, v: number): string {
    return R.formatearDinero(v, p.monedaMonto);
  }

  /** Prefijo del input del simulador. */
  prefijo(p: ProductoTasa): string {
    return p.monedaMonto === 'VES' ? 'Bs.' : p.monedaMonto;
  }

  // ───────────────────────── Navegación ─────────────────────────

  navegable(t: TasaHome): boolean {
    return !this.soloPublicas && (!!t.def.destino || !!t.def.ruta);
  }

  /**
   * Abre el destino de la fila. Los envíos van al cotizador con el par ya
   * elegido; las conversiones, a su propia pantalla. En ningún caso se
   * precarga un monto.
   */
  abrir(t: TasaHome): void {
    if (!this.navegable(t)) return;

    if (t.def.destino) {
      this.router.navigate(['/wallet/client/enviar'], {
        queryParams: { origen: t.def.origen, destino: t.def.destino },
      });
      return;
    }

    if (t.def.ruta) this.router.navigate([t.def.ruta]);
  }

}

/** «1.500.000» → 1500000; «1.500,5» → 1500.5. Lo que no es número, 0. */
export function parsearMonto(texto: string | null | undefined): number {
  const limpio = String(texto ?? '').replace(/[^\d,]/g, '').replace(',', '.');
  const n = parseFloat(limpio);
  return Number.isFinite(n) && n > 0 ? n : 0;
}
